import type { Chart, ChartEdge, ChartNode } from './api'

export const NODE_WIDTH = 220
export const NODE_HEIGHT = 64

// Отступы подобраны так, чтобы полупрозрачный фон (см. PositionedBackground) был
// виден полосой вокруг карточек, а не тонкой линией в 1-2px: иначе в тёмной теме
// он читается как случайные линии, а не как прямоугольник.
const GROUP_PADDING = 22
const HEADER_GAP = 18
const SIBLING_GAP = 20
const ROOT_GAP = 56
const ADVISOR_GAP = 24

/** Тип связи OrgLink, при котором сотрудник рисуется не в дереве, а рядом с адресатом. */
export const ADVISOR_LINK_TYPE = 'советник'

export interface PositionedNode extends ChartNode {
  /** Абсолютные координаты на канве (не относительно родителя). */
  x: number
  y: number
  width: number
  height: number
  parentId: string | null
  hasChildren: boolean
  collapsed: boolean
}

/**
 * Полупрозрачный фон под руководителем и всеми его подчинёнными. Сам сотрудник —
 * всегда обычная карточка фиксированного размера (см. PositionedNode); фон — отдельный
 * декоративный слой под ней, вложенные фоны накладываются друг на друга.
 */
export interface PositionedBackground {
  id: string
  business_line: string | null
  /** Ключ палитры направления — фон красится приглушённой версией того же цвета. */
  color: string
  x: number
  y: number
  width: number
  height: number
  parentId: string | null
}

/** Кто чей потомок по управленческим рёбрам. Кросс-связи дерево не образуют. */
function childrenIndex(edges: ChartEdge[]): Map<string, string[]> {
  const index = new Map<string, string[]>()
  for (const edge of edges) {
    if (edge.kind !== 'management') continue
    const list = index.get(edge.source) ?? []
    list.push(edge.target)
    index.set(edge.source, list)
  }
  return index
}

/**
 * Узлы, скрытые схлопнутыми ветками.
 *
 * Скрывается всё поддерево, а не только прямые потомки: иначе «свернуть» у корня
 * оставляло бы висеть внуков без родителя.
 */
export function hiddenNodes(edges: ChartEdge[], collapsed: Set<string>): Set<string> {
  const children = childrenIndex(edges)
  const hidden = new Set<string>()

  const walk = (id: string) => {
    for (const child of children.get(id) ?? []) {
      if (hidden.has(child)) continue
      hidden.add(child)
      walk(child)
    }
  }

  for (const id of collapsed) walk(id)
  return hidden
}

interface Box {
  nodeId: string
  width: number
  height: number
  /** Есть развёрнутые подчинённые → под сотрудником рисуется общий фон. */
  isGroup: boolean
  children: { relX: number; relY: number; box: Box }[]
}

/**
 * Высота карточки.
 *
 * Обычно постоянна, но карточка, на которую легли шаги процесса, раскрывает под ними поле
 * с кружками и становится выше. Высота обязана учитываться именно здесь, в раскладке:
 * растяни карточку только в CSS — и она налезет на соседей, потому что все координаты
 * посчитаны от фиксированной величины.
 */
type HeightOf = (nodeId: string) => number

const constantHeight: HeightOf = () => NODE_HEIGHT

/**
 * Руководитель с развёрнутыми подчинёнными занимает прямоугольник: сам он — карточка
 * по центру сверху, подчинённые — под ней. Подчинённый, у которого есть свои
 * подчинённые, сам занимает такой же прямоугольник — отсюда вложенность произвольной
 * глубины (см. flatten, где на каждый такой узел создаётся отдельный фон-подложка).
 *
 * Подчинённые бывают двух родов: «подразделения» — структурные единицы (свёрнутый узел или
 * руководитель со своими людьми, то есть сам занимающий прямоугольник) — и отдельные
 * сотрудники без своей структуры. Когда подразделений двое или меньше, всё содержимое
 * выстраивается в столбец, а не в ряд: иначе типичный случай «один-два отдела и пара
 * сотрудников» давал бы неоправданно широкий и низкий прямоугольник. Ряд остаётся
 * только там, где структурных единиц действительно много (например, у гендиректора).
 */
function buildBox(
  id: string,
  children: Map<string, string[]>,
  collapsed: Set<string>,
  nodeById: Map<string, ChartNode>,
  heightOf: HeightOf,
): Box {
  const node = nodeById.get(id)
  // Свёрнутый узел — всегда лист: это агрегат по численности, а не человек с подчинёнными.
  const kidIds = node?.kind === 'department' ? [] : (children.get(id) ?? [])
  const expanded = kidIds.length > 0 && !collapsed.has(id)

  if (!expanded) {
    return { nodeId: id, width: NODE_WIDTH, height: heightOf(id), isGroup: false, children: [] }
  }

  const kidBoxes = kidIds.map((kidId) =>
    buildBox(kidId, children, collapsed, nodeById, heightOf),
  )
  const structuralUnits = kidBoxes.filter(
    (box) => nodeById.get(box.nodeId)?.kind === 'department' || box.isGroup,
  ).length
  const vertical = structuralUnits <= 2

  const startY = GROUP_PADDING + heightOf(id) + HEADER_GAP
  let placed: { relX: number; relY: number; box: Box }[]
  let width: number
  let height: number

  if (vertical) {
    const contentWidth = Math.max(NODE_WIDTH, ...kidBoxes.map((box) => box.width))
    let cursor = startY
    placed = kidBoxes.map((box) => {
      const relY = cursor
      cursor += box.height + SIBLING_GAP
      return { relX: GROUP_PADDING + (contentWidth - box.width) / 2, relY, box }
    })
    width = contentWidth + GROUP_PADDING * 2
    height = cursor - SIBLING_GAP + GROUP_PADDING
  } else {
    let cursor = GROUP_PADDING
    placed = kidBoxes.map((box) => {
      const relX = cursor
      cursor += box.width + SIBLING_GAP
      return { relX, relY: startY, box }
    })
    const contentWidth = Math.max(NODE_WIDTH, cursor - SIBLING_GAP - GROUP_PADDING)
    const rowHeight = Math.max(...kidBoxes.map((box) => box.height))
    width = contentWidth + GROUP_PADDING * 2
    height = startY + rowHeight + GROUP_PADDING
  }

  return { nodeId: id, width, height, isGroup: true, children: placed }
}

/**
 * Советники не подчиняются, а стоят рядом с адресатом связи — справа от него,
 * друг под другом, если советников несколько. Кладём их сюда же, а не в общий
 * ряд/столбец подчинённых, чтобы получить точку (absX, absY) и ширину бокса адресата,
 * от которых советники отсчитывают своё место.
 */
function placeAdvisors(
  leaderId: string,
  boxWidth: number,
  absX: number,
  absY: number,
  parentBackgroundId: string | null,
  advisorsByLeader: Map<string, string[]>,
  nodeById: Map<string, ChartNode>,
  heightOf: HeightOf,
  nodesOut: PositionedNode[],
) {
  const advisorIds = advisorsByLeader.get(leaderId)
  if (!advisorIds) return

  // Курсор, а не index * шаг: советники могут быть разной высоты, если на них легли
  // шаги процесса, и постоянный шаг наложил бы их друг на друга.
  let cursor = absY + GROUP_PADDING
  for (const advisorId of advisorIds) {
    const advisorNode = nodeById.get(advisorId)
    if (!advisorNode) continue
    const height = heightOf(advisorId)
    nodesOut.push({
      ...advisorNode,
      x: absX + boxWidth + ADVISOR_GAP,
      y: cursor,
      width: NODE_WIDTH,
      height,
      parentId: parentBackgroundId,
      hasChildren: false,
      collapsed: false,
    })
    cursor += height + ADVISOR_GAP
  }
}

function flatten(
  box: Box,
  absX: number,
  absY: number,
  parentBackgroundId: string | null,
  nodeById: Map<string, ChartNode>,
  collapsed: Set<string>,
  children: Map<string, string[]>,
  advisorsByLeader: Map<string, string[]>,
  heightOf: HeightOf,
  nodesOut: PositionedNode[],
  backgroundsOut: PositionedBackground[],
) {
  const node = nodeById.get(box.nodeId)
  if (!node) return

  if (box.isGroup) {
    const backgroundId = `bg-${box.nodeId}`
    backgroundsOut.push({
      id: backgroundId,
      business_line: node.business_line,
      color: node.color,
      x: absX,
      y: absY,
      width: box.width,
      height: box.height,
      parentId: parentBackgroundId,
    })

    // Сам руководитель — обычная карточка по центру сверху над своим фоном.
    nodesOut.push({
      ...node,
      x: absX + (box.width - NODE_WIDTH) / 2,
      y: absY + GROUP_PADDING,
      width: NODE_WIDTH,
      height: heightOf(box.nodeId),
      parentId: backgroundId,
      hasChildren: true,
      collapsed: false,
    })

    // Советники — справа от всего прямоугольника (не от карточки руководителя
    // внутри него), то есть заметно снаружи фона, а не «под ним» среди подчинённых.
    placeAdvisors(
      box.nodeId,
      box.width,
      absX,
      absY,
      parentBackgroundId,
      advisorsByLeader,
      nodeById,
      heightOf,
      nodesOut,
    )

    for (const child of box.children) {
      flatten(
        child.box,
        absX + child.relX,
        absY + child.relY,
        backgroundId,
        nodeById,
        collapsed,
        children,
        advisorsByLeader,
        heightOf,
        nodesOut,
        backgroundsOut,
      )
    }
    return
  }

  nodesOut.push({
    ...node,
    x: absX,
    y: absY,
    width: box.width,
    height: box.height,
    parentId: parentBackgroundId,
    hasChildren: (children.get(box.nodeId) ?? []).length > 0,
    collapsed: collapsed.has(box.nodeId),
  })

  placeAdvisors(
    box.nodeId,
    box.width,
    absX,
    absY,
    parentBackgroundId,
    advisorsByLeader,
    nodeById,
    heightOf,
    nodesOut,
  )
}

export function layout(
  chart: Chart,
  collapsed: Set<string>,
  heightOf: HeightOf = constantHeight,
) {
  const hidden = hiddenNodes(chart.edges, collapsed)
  const children = childrenIndex(chart.edges)

  const visibleNodes = chart.nodes.filter((node) => !hidden.has(node.id))
  const nodeById = new Map(visibleNodes.map((node) => [node.id, node]))

  // Вложенность уже показывает подчинение, поэтому отдельное управленческое ребро
  // не рисуется — общий фон под руководителем и его людьми заменяет его собой.
  const managedTargets = new Set(
    chart.edges.filter((edge) => edge.kind === 'management').map((edge) => edge.target),
  )

  // Советник не подчиняется никому, но и не встаёт в общий ряд корней — его место
  // определяется адресатом связи (см. placeAdvisors), поэтому он тоже исключается
  // из списка корней, хотя формально там оказался бы (нет управленческого ребра).
  const advisorsByLeader = new Map<string, string[]>()
  const advisorSourceIds = new Set<string>()
  for (const edge of chart.edges) {
    if (edge.kind !== 'cross' || edge.link_type !== ADVISOR_LINK_TYPE) continue
    const list = advisorsByLeader.get(edge.target) ?? []
    list.push(edge.source)
    advisorsByLeader.set(edge.target, list)
    advisorSourceIds.add(edge.source)
  }

  const roots = visibleNodes.filter(
    (node) => !managedTargets.has(node.id) && !advisorSourceIds.has(node.id),
  )

  const nodes: PositionedNode[] = []
  const backgrounds: PositionedBackground[] = []
  let x = 0
  for (const root of roots) {
    const box = buildBox(root.id, children, collapsed, nodeById, heightOf)
    flatten(
      box,
      x,
      0,
      null,
      nodeById,
      collapsed,
      children,
      advisorsByLeader,
      heightOf,
      nodes,
      backgrounds,
    )
    const advisorCount = advisorsByLeader.get(root.id)?.length ?? 0
    const advisorWidth = advisorCount > 0 ? ADVISOR_GAP + NODE_WIDTH : 0
    x += box.width + advisorWidth + ROOT_GAP
  }

  return { nodes, backgrounds }
}

/** Цепочка предков — нужна, чтобы поиск разворачивал ветку до найденного узла. */
export function ancestorsOf(edges: ChartEdge[], nodeId: string): string[] {
  const parents = new Map<string, string>()
  for (const edge of edges) {
    if (edge.kind === 'management') parents.set(edge.target, edge.source)
  }

  const chain: string[] = []
  const seen = new Set<string>([nodeId])
  let current = parents.get(nodeId)
  while (current && !seen.has(current)) {
    chain.push(current)
    seen.add(current)
    current = parents.get(current)
  }
  return chain
}
